/**
 * collectPages walks an offset-paginated endpoint to the end and returns every
 * row, de-duplicated on `id`.
 *
 * It exists for the filter lookups that must be COMPLETE to be correct: a
 * childcare filter built on a partial membership list hides children from a
 * money screen without saying so. Anything less than the whole set is an error
 * here, never a shorter answer.
 *
 * Two failure modes it has to survive, both real:
 *
 *  - A tie in the endpoint's ORDER BY. Offset paging over rows the database
 *    considers equal can hand the same row back on the next page. The offset
 *    therefore advances by what the SERVER returned, not by how many rows
 *    survived de-duplication — rewinding it by the duplicates would re-request
 *    the same window forever — and a repeat is absorbed rather than fatal.
 *    (The queries this reaches now carry an id tiebreaker, so a repeat should
 *    no longer happen; the tolerance is what keeps an untiebroken caller
 *    correct rather than broken.)
 *
 *  - An endpoint that ignores `offset` altogether. That looks the same on the
 *    first page and never terminates, so a page that contributes NOTHING new
 *    is treated as a stalled cursor and throws.
 */

/** Requests one collection can take before the walk is called stalled. */
const MAX_PAGE_REQUESTS = 200;

export async function collectPages<T extends { id?: string }>(
  load: (offset: number) => Promise<{ items: T[]; total?: number }>,
): Promise<T[]> {
  const items: T[] = [];
  const seen = new Set<string>();
  let offset = 0;
  for (let request = 0; request < MAX_PAGE_REQUESTS; request += 1) {
    const page = await load(offset);
    const rows = page.items;
    if (!rows.length) {
      // An empty page short of `total` means the endpoint stopped serving rows
      // it still counts. Returning what we have would quietly drop members, so
      // this fails loudly and the caller renders an error instead of a filter
      // that is wrong by omission.
      if (page.total !== undefined && offset < page.total)
        throw new Error("Incomplete filter data");
      return items;
    }
    offset += rows.length;
    let added = 0;
    for (const row of rows) {
      if (!row.id) throw new Error("Repeated or invalid filter data");
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      items.push(row);
      added += 1;
    }
    if (page.total !== undefined && offset >= page.total) {
      // The server served `total` rows but fewer than `total` DISTINCT ones, so
      // a shuffle re-sent one row in place of another we never saw. Returning
      // here would hand back a membership list that is short by exactly the
      // rows it skipped — the silent under-count this helper exists to prevent.
      if (items.length < page.total)
        throw new Error("Incomplete filter data");
      return items;
    }
    // A full page of rows we already hold: the cursor is not moving.
    if (!added) throw new Error("Repeated or invalid filter data");
  }
  throw new Error("Repeated or invalid filter data");
}
